import { Box, Card, CardContent, SvgIcon, Tooltip as TileTooltip, Typography } from '@mui/material';
import { ChartBoxOutline, ImageOutline, PlayCircle, Speedometer, Table, Xml } from '@opengov/react-capital-assets';
import { FULL_WIDTH, THREE_COLUMNS_1, THREE_COLUMNS_3 } from '@opengov/stories-shared/constants';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React from 'react';
import { IEditorMessages } from '../../../../types';
import { IUpdateStoryContentProps } from '../../../../types/storyViewTypes';
import { REPORT_TABLE_MAX_LIMIT } from '../../../constants';
import { ComponentErrorBoundary } from '../../ErrorBoundary/ComponentErrorBoundary';
import { ErrorToastFallbackComponent } from '../../ErrorBoundary/ToastFallbackComponent';
import { AddReportTableModal } from '../../Publications/CustomReports/AddReportTableModal/AddReportTableModal';
import { useModal } from '../../StoryView/StoryView/contexts/ModalContext';
import { getMediaIndex } from '../../../utils/mediaIndex.utils';
import { styles } from './MediaBlock.styles';

export interface IMediaBlockProps {
  messages: IEditorMessages;
  format: string;
  totalReportTables: number;
  handleImage: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleVideo: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleEmbed: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleReport: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleReportTable: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleProgressTracker: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  showProgressTracker: boolean;
  isTemplate: boolean;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContent: IStoryContent;
  publicationId: string | undefined;
  isCustomSpreadsheetReportEnabled: boolean;
  updateStoryContent: (storyContent: IStoryContent) => void;
  updateStoryContentDB: (props: IUpdateStoryContentProps) => void;
}

const TileContent = ({ icon, title }: { icon: React.ElementType; title: string }) => (
  <>
    <SvgIcon component={icon} inheritViewBox fontSize="small" />
    <Typography variant="body2">{title}</Typography>
  </>
);

const renderContentTile = (handleTileClick, title, icon, messages, totalReportTables = 0) => {
  const isDisabled = totalReportTables >= REPORT_TABLE_MAX_LIMIT;
  const cardContent = (
    <Card sx={isDisabled ? styles.disabledTileCard : styles.tileCard}>
      <CardContent onClick={isDisabled ? undefined : handleTileClick}>
        <TileContent icon={icon} title={title} />
      </CardContent>
    </Card>
  );

  return isDisabled ? (
    <TileTooltip title={messages.disabledReportTable} placement="top">
      {cardContent}
    </TileTooltip>
  ) : (
    cardContent
  );
};

const isFullWidthTileDisplayed = (format) => format === FULL_WIDTH;

export const MediaBlock: React.FC<IMediaBlockProps> = ({
  totalReportTables,
  messages,
  format,
  handleImage,
  handleVideo,
  handleEmbed,
  handleReport,
  handleReportTable,
  showProgressTracker,
  handleProgressTracker,
  isCustomSpreadsheetReportEnabled,
  entityId,
  entitySubdomain,
  story,
  storyContent,
  publicationId,
  updateStoryContent,
  updateStoryContentDB,
  isTemplate
}) => {
  const { setCurrentModal } = useModal();

  const isNonEmptyPublicationId = (publicationId: string | undefined): publicationId is string => {
    return !isNil(publicationId) && !isEmpty(publicationId);
  };

  const handleDualTabReportTableModal = (event: React.MouseEvent<HTMLElement>) => {
    if (!isNonEmptyPublicationId(publicationId)) {
      return;
    }

    setCurrentModal(
      <ComponentErrorBoundary
        FallbackComponent={() => (
          <ErrorToastFallbackComponent
            title={messages.somethingWentWrongError}
            body={messages.unableToLoadCustomReports}
          />
        )}
      >
        <AddReportTableModal
          isOpen={true}
          onClose={() => setCurrentModal(null)}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          story={story}
          storyContent={storyContent}
          mediaIndex={getMediaIndex(event.target as HTMLElement)}
          publicationId={publicationId}
          isTemplate={isTemplate}
          updateStoryContent={updateStoryContent}
          updateStoryContentDB={updateStoryContentDB}
        />
      </ComponentErrorBoundary>
    );
  };

  // Per Requirements, this feature is available only if the story is associated with a publication.
  const shouldShowCustomSpreadsheetFeature =
    isCustomSpreadsheetReportEnabled && isFullWidthTileDisplayed(format) && isNonEmptyPublicationId(publicationId);

  const addPadding = format === THREE_COLUMNS_1 || format === THREE_COLUMNS_3;

  return (
    <Box>
      <Box sx={styles.mediaBlockContainer}>
        <Box sx={styles.mediaBlockLabel}>
          <Typography component="span" variant="h4">
            Select media to upload
          </Typography>
        </Box>
        <Box
          sx={
            addPadding
              ? { ...styles.mediaBlockTileContainerBase, ...styles.mediaBlockTileContainer }
              : styles.mediaBlockTileContainerBase
          }
        >
          {renderContentTile(handleReport, messages.reportGraph, ChartBoxOutline, messages)}

          {isFullWidthTileDisplayed(format)
            ? shouldShowCustomSpreadsheetFeature
              ? renderContentTile(
                  handleDualTabReportTableModal,
                  messages.reportTable,
                  Table,
                  messages,
                  totalReportTables
                )
              : renderContentTile(handleReportTable, messages.reportTable, Table, messages, totalReportTables)
            : null}

          {renderContentTile(handleImage, messages.image, ImageOutline, messages)}
          {renderContentTile(handleVideo, messages.video, PlayCircle, messages)}
          {renderContentTile(handleEmbed, messages.embed, Xml, messages)}
          {/* Handler to be added when the timeline chart tile is implemented */}
          {isFullWidthTileDisplayed(format) && showProgressTracker
            ? renderContentTile(handleProgressTracker, messages.progressTracker, Speedometer, messages)
            : null}
        </Box>
      </Box>
    </Box>
  );
};
